/**
 * Regola di listino dei noleggi, gemella di calculatePrice() lato server
 * (RentalReservationController, sia api sia dash).
 *
 * Ogni scaglione esprime il prezzo totale della sua durata, non un supplemento:
 * la tariffa giornaliera è `prezzo / giorni` e vale per l'intero noleggio. Si
 * applica lo scaglione più profondo valorizzato che non superi la durata, e
 * quelli lasciati vuoti si saltano scendendo al più vicino sotto.
 *
 * Vive qui e non nei componenti perché la stessa regola serviva a tre schermate
 * che ne tenevano tre copie divergenti: il listino con un "NaN€" al posto dello
 * scaglione vuoto veniva proprio da quelle copie disallineate.
 */

const TIER_FIELDS = [
  [1, 'price_1_day'],
  [2, 'price_2_days'],
  [3, 'price_3_days'],
];

/**
 * Importo valorizzato, altrimenti null. Gli scaglioni non compilati arrivano
 * dall'API come `null` (la colonna è nullable) e i valorizzati come stringhe
 * decimali; lo zero vale come "non compilato", che è come lo tratta il server.
 */
function amount(value) {
  const parsed = parseFloat(value);

  return isFinite(parsed) && parsed > 0 ? parsed : null;
}

function roundToCents(value) {
  return Math.round(value * 100) / 100;
}

/** Gli scaglioni effettivamente valorizzati, dal più breve al più lungo. */
export function rentalTiers(device) {
  const source = device || {};

  return TIER_FIELDS
    .map(([days, field]) => ({ days, price: amount(source[field]) }))
    .filter((tier) => tier.price !== null);
}

/** Totale del noleggio, o null se il dispositivo non ha un listino utilizzabile. */
export function rentalTotal(device, days) {
  const source = device || {};

  if (source.price_type === 'single') {
    const price = parseFloat(source.price);

    return isFinite(price) ? price : 0;
  }

  const duration = Number(days);

  if (!isFinite(duration) || duration < 1) return null;

  const applicable = rentalTiers(source).filter((tier) => tier.days <= duration);
  const tier = applicable[applicable.length - 1];

  // Arrotondamento sul totale e non sulla tariffa: 25€/3gg sono 8,3333…€ al
  // giorno, che su 10 giorni fanno 83,33 e non 83,30.
  if (tier) return roundToCents((tier.price / tier.days) * duration);

  const basePrice = amount(source.price);

  if (basePrice !== null) return roundToCents(basePrice * duration);

  return null;
}
